import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

interface DurationInputProps {
  value: number;
  onChange: (val: number) => void;
  className?: string;
  compact?: boolean;
}

export function DurationInput({ value, onChange, className, compact }: DurationInputProps) {
  const quickOptions = [0.5, 1, 2, 4];
  
  const hours = Math.floor(value);
  const minutes = Math.round((value - hours) * 60);

  const handleHoursChange = (newHours: number) => {
    onChange(Math.max(0, newHours) + minutes / 60);
  };

  const handleMinutesChange = (newMinutes: number) => {
    const clampedMinutes = Math.max(0, Math.min(59, newMinutes));
    onChange(hours + clampedMinutes / 60);
  };

  if (compact) {
    return (
      <div className={cn("flex items-center gap-1", className)}>
        <Input 
          type="number" 
          step="1"
          min="0"
          value={hours} 
          onChange={(e) => handleHoursChange(parseInt(e.target.value) || 0)}
          className="w-12 h-9 font-mono text-center text-sm px-1" 
          data-testid="input-duration-hours"
        />
        <span className="text-xs text-muted-foreground">h</span>
        <Input 
          type="number" 
          step="1"
          min="0"
          max="59"
          value={minutes} 
          onChange={(e) => handleMinutesChange(parseInt(e.target.value) || 0)}
          className="w-12 h-9 font-mono text-center text-sm px-1" 
          data-testid="input-duration-minutes"
        />
        <span className="text-xs text-muted-foreground">m</span>
      </div>
    );
  }

  return (
    <div className={cn("space-y-2", className)}>
      <div className="flex items-center gap-2">
        <Input 
          type="number" 
          step="1"
          min="0"
          value={hours} 
          onChange={(e) => handleHoursChange(parseInt(e.target.value) || 0)}
          className="w-16 font-mono text-center" 
          data-testid="input-duration-hours"
        />
        <span className="text-sm text-muted-foreground font-medium">h</span>
        <Input 
          type="number" 
          step="1"
          min="0"
          max="59"
          value={minutes} 
          onChange={(e) => handleMinutesChange(parseInt(e.target.value) || 0)}
          className="w-16 font-mono text-center" 
          data-testid="input-duration-minutes"
        />
        <span className="text-sm text-muted-foreground font-medium">m</span>
      </div>
      <div className="flex gap-2">
        {quickOptions.map((opt) => (
          <Button
            key={opt}
            type="button"
            variant={value === opt ? "default" : "outline"}
            size="sm"
            onClick={() => onChange(opt)}
            className={cn(
                "h-7 px-2 text-xs transition-all",
                value === opt ? "bg-primary text-primary-foreground shadow-md scale-105" : "text-muted-foreground hover:text-foreground"
            )}
          >
            +{opt}h
          </Button>
        ))}
      </div>
    </div>
  );
}
